import { Flex, Icon, IconTypes, Rte, breakpoints, colors, isIconType, Text } from '@capioweb/ui-components/src'
import { ImageStackContentAreaProperty } from './ImageStackBlock'
import styled from 'styled-components'
import { QBankImage } from '../../@types/CapioWeb/core'
import { createMarkup, imageUrlToImageProps } from '../../utils'
import React from 'react'

interface ImageStackBlockLayoutsProps {
    items: ImageStackContentAreaProperty
    value: string
}
interface IconWrapperProps {
    ratio: string
}

interface StyledIconWrapperProps {
    ratio: string
    color: string
}
interface SquareBoxWrapperProps {
    color: string
}

const StyledIcon = styled.div<IconWrapperProps>`
    margin: auto;

    &svg {
        width: 100%;
        height: 100%;
    }
    `

const StyledBoxWrapper = styled.div<StyledIconWrapperProps>`
    width: 100%;
    height: auto;
    background-color: ${(props) => colors[props.color]};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    aspect-ratio: ${(props) => props.ratio};
    padding: 24px;
    overflow: visible;
    flex-grow: 1;

    @media screen and (min-width: ${breakpoints.m}px) {  
        padding: 56px;
    }
`

const SquareBoxWrapper = styled.div<SquareBoxWrapperProps>`
    position: relative;
    width: 100%;
    background-color: ${(props) => colors[props.color]};
    min-height: auto;
    aspect-ratio: 1/1;
    overflow: visible;
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-grow: 1;
`

interface StyledRteProps {
    hasIconOrDisplayText?: boolean
}

const StyledRte = styled(Rte) <StyledRteProps>`
    width: 100%;
    text-align: ${(props) => props.hasIconOrDisplayText && 'center'};
`

const srcSetSizes = [{ width: 400, breakpoint: 2560 }]

const getIcon = (icon: string): IconTypes | undefined => {
    if (isIconType(icon)) {
        return icon as IconTypes
    }
    return undefined
}

const ImageWrapper = (image: QBankImage, ratio: string) => {
    return (
        <Flex
            w={1}
            h={1}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            style={{ aspectRatio: ratio }}
        >
            <img
                {...imageUrlToImageProps(
                    `${image.media.value.url}?format=webp`,
                    image.imageTitle?.value,
                    image.alternateText?.value,
                    true,
                    true,
                    srcSetSizes
                )}
                width="100%"
                height="100%"
                style={{ objectFit: "cover" }}
            />
        </Flex>
    )
}

const IconWrapper = (value: string, color: string, icon: string, ratio: string, iconText: string, displayText: string) => {

    //Desktop: Is the box small (3-in-a-row)
    const smallBoxes = value === '3' || value === '7'

    const iconSizeSquare = iconText
        ? smallBoxes ? 10 : 13
        : smallBoxes ? 16 : 25


    return (
        <>
            {ratio !== '1/1'
                ? <StyledBoxWrapper ratio={ratio} color={color}>
                    {icon &&
                        <Flex flexDirection='column' w={1}>
                            <StyledIcon ratio={ratio}>
                                <Icon
                                    icon={getIcon(icon)}
                                    color='navy'
                                    size={{
                                        _: iconText
                                            ? itemStatsMobile(value).iconSizeWithText
                                            : itemStatsMobile(value).iconSizeWithoutText,
                                        m: iconText
                                            ? 17
                                            : itemStatsDesktop(value).aspectRatio === '31/15' ? 30 : 45
                                    }}
                                ></Icon>
                            </StyledIcon>
                        </Flex>
                    }
                    {displayText &&
                        <Text textAlign='center' fontSize={{ _: 4, m: 8 }} lineHeight={{ _: 4, m: 8 }} fontWeight='bold'>{displayText}</Text>
                    }
                    {iconText &&
                        <Flex w={1} display='block'>
                            <StyledRte hasIconOrDisplayText={!!icon || !!displayText}>
                                <div dangerouslySetInnerHTML={createMarkup(iconText)}></div>
                            </StyledRte>
                        </Flex>
                    }
                </StyledBoxWrapper >
                : <SquareBoxWrapper color={color}>
                    <Flex flexDirection='column' p={smallBoxes ? 2 : 3}>
                        <Flex flexDirection="column" alignItems="center">
                            {icon &&
                                <StyledIcon ratio={ratio}>
                                    <Icon
                                        icon={getIcon(icon)}
                                        color='navy'
                                        size={{ _: iconText ? 9 : 12, m: iconSizeSquare }}
                                    ></Icon>
                                </StyledIcon>
                            }
                            {displayText &&
                                <Text textAlign='center' fontSize={{ _: 4, m: 8 }} lineHeight={{ _: 4, m: 8 }} fontWeight='bold'>{displayText}</Text>
                            }
                            <Flex w={1} flexDirection="column" h={1}>
                                <StyledRte hasIconOrDisplayText={!!icon || !!displayText}>
                                    <div dangerouslySetInnerHTML={createMarkup(iconText)}></div>
                                </StyledRte>
                            </Flex>
                        </Flex>
                    </Flex>
                </SquareBoxWrapper>
            }
        </>
    )
}

const itemStatsMobile = (value: string) => {

    switch (value) {
        case "1":
            return { aspectRatio: "57/60", maxItems: 1, startIndex: 0, iconSizeWithText: 13, iconSizeWithoutText: 25 }
        case "2":
            return { aspectRatio: "171/112", maxItems: 1, startIndex: 0, iconSizeWithText: 11, iconSizeWithoutText: 15 }
        case "3":
            return { aspectRatio: "171/112", maxItems: 4, startIndex: 2, iconSizeWithText: 11, iconSizeWithoutText: 15 }
        case "4":
            return { aspectRatio: "171/112", maxItems: 3, startIndex: 1, iconSizeWithText: 11, iconSizeWithoutText: 15 }
        case "5":
            return { aspectRatio: "1/1", maxItems: 2, startIndex: 0, iconSizeWithText: 0, iconSizeWithoutText: 0 }
        case "6":
            return { aspectRatio: "", maxItems: 4, startIndex: 0, iconSizeWithText: 0, iconSizeWithoutText: 0 }
        case "7":
            return { aspectRatio: "171/80", maxItems: 3, startIndex: 1, iconSizeWithText: 9, iconSizeWithoutText: 13 }
        default:
            return { aspectRatio: "", maxItems: 1, startIndex: 0, iconSizeWithText: 1, iconSizeWithoutText: 1 }
    }
}

const itemStatsDesktop = (value: string) => {

    switch (value) {
        case "1":
            return { aspectRatio: "31/23", maxItems: 1, squaredImageWidth: "" }
        case "2":
            return { aspectRatio: "31/15", maxItems: 1, squaredImageWidth: "" }
        case "3":
            return { aspectRatio: "31/15", maxItems: 4, squaredImageWidth: "calc(100% / 3 - 16px)" }
        case "4":
            return { aspectRatio: "31/15", maxItems: 3, squaredImageWidth: "calc(50% - 12px)" }
        case "5":
            return { aspectRatio: "1/1", maxItems: 2, squaredImageWidth: "calc(50% - 12px)" }
        case "6":
            return { aspectRatio: "", maxItems: 4, squaredImageWidth: "calc(50% - 12px)" }
        case "7":
            return { aspectRatio: "", maxItems: 3, squaredImageWidth: "calc(100% / 3 - 16px)" }
        default:
            return { aspectRatio: "", maxItems: 1, squaredImageWidth: "" }
    }
}


const LayoutsDesktop: React.FC<ImageStackBlockLayoutsProps> = ({ items, value }) => {


    //If no items are set, return null
    if (!items.value) return null
    //If value if not set, set it to 1
    if (!value) value = '1'

    return <>
        {/* Big boxm first child */}
        {Number(value) <= 4 &&
            <Flex
                w={1}
            >
                {items.value[0].image?.media?.value
                    ? ImageWrapper(items.value[0].image, itemStatsDesktop(value).aspectRatio)
                    : IconWrapper(value, items.value[0].backgroundColor?.value, items.value[0].icon?.value, itemStatsDesktop(value).aspectRatio, items.value[0].iconText?.value, items.value[0].displayText?.value)}
            </Flex>
        }
        {/* Small squared boxes */}
        {Number(value) >= 3 && items.value.length > 1 &&
            items.value.slice(Number(value) <= 4 ? 1 : 0, itemStatsDesktop(value).maxItems).map((item, index) => {
                return <Flex
                    key={index}
                    w={itemStatsDesktop(value).squaredImageWidth}
                >
                    {item.image?.media?.value
                        ? ImageWrapper(item.image, itemStatsDesktop('5').aspectRatio)
                        : IconWrapper(value, item.backgroundColor?.value, item.icon?.value, itemStatsDesktop('5').aspectRatio, item.iconText?.value, item.displayText?.value)}
                </Flex>
            })
        }
    </>
}
const LayoutsMobile: React.FC<ImageStackBlockLayoutsProps> = ({ items, value }) => {

    //If no items are set, return null
    if (!items.value) return null
    //If value if not set, set it to 1
    if (!value) value = '1'
    return <>
        {/* Big box (first child, and second child when layout value is 3) */}
        {(Number(value) <= 4 || value === '7') &&
            <>
                <Flex
                    w={1}
                    display="block"
                >
                    {items.value[0].image?.media?.value
                        ? ImageWrapper(items.value[0].image, itemStatsMobile(value).aspectRatio)
                        : IconWrapper(value, items.value[0].backgroundColor?.value, items.value[0].icon?.value, itemStatsMobile(value).aspectRatio, items.value[0].iconText?.value, items.value[0].displayText?.value)}
                </Flex>
                {value === '3' &&
                    <Flex
                        w={1}
                        display="block"
                    >
                        {items.value[1].image?.media?.value
                            ? ImageWrapper(items.value[1].image, itemStatsMobile('7').aspectRatio)
                            : IconWrapper(value, items.value[1].backgroundColor?.value, items.value[1].icon?.value, itemStatsMobile('7').aspectRatio, items.value[1].iconText?.value, items.value[1].displayText?.value)}
                    </Flex>
                }
            </>
        }
        {/* Small squared boxes */}
        {Number(value) >= 3 && items.value.length > 1 &&
            items.value.slice(itemStatsMobile(value).startIndex, itemStatsMobile(value).maxItems).map((item, index) => {

                return <Flex
                    key={index}
                    w="calc(50% - 8px)"
                >
                    {item.image?.media?.value
                        ? ImageWrapper(item.image, itemStatsMobile('5').aspectRatio)
                        : IconWrapper(value, item.backgroundColor?.value, item.icon?.value, itemStatsMobile('5').aspectRatio, item.iconText?.value, item.displayText?.value)}
                </Flex>
            })

        }
    </>
}

export { LayoutsDesktop, LayoutsMobile }